import React from 'react';
import { useMutation } from '@tanstack/react-query';

function adminHeaders() {
  try {
    const isAuthed = typeof window !== 'undefined' && localStorage.getItem('fca_admin_authed_v1') === '1';
    const headers = isAuthed ? { 'x-admin-ok': '1' } : {};
    console.log(`[ADVANCED TAB AUTH] State: ${isAuthed ? 'Authorized' : 'Unauthorized'} | Headers:`, headers); // ADDED LOG
    return headers;
  } catch (e) { 
    console.error('[ADVANCED TAB AUTH] Error reading localStorage:', e); // ADDED LOG
    return {}; 
  }
}

async function postJSON(body: any) {
  const r = await fetch('/api/seo', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...adminHeaders() },
    body: JSON.stringify(body),
  });
  const j = await r.json();
  if (!j.ok) throw new Error(j.error || 'failed');
  return j;
}

export function AdvancedTab() {
  const [robots, setRobots] = React.useState<string>('');
  const [note, setNote] = React.useState<string | null>(null);

  // Load robots on mount
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const j = await postJSON({ action: 'robots.get' });
        if (!live) return;
        setRobots(String(j.robots || ''));
      } catch (e: any) {
        setNote(e?.message || 'Failed to load robots.txt');
      }
    })();
    return () => { live = false; };
  }, []);

  const saveRobots = useMutation({
    mutationFn: async () => postJSON({ action: 'robots.save', robots }),
    onSuccess: () => setNote('robots.txt saved.'),
    onError: (e: any) => setNote(e?.message || 'Save failed'),
  });

  const genSitemap = useMutation({
    mutationFn: async () => postJSON({ action: 'sitemap.generate' }),
    onSuccess: () => setNote('Sitemap regenerated.'),
    onError: (e: any) => setNote(e?.message || 'Sitemap failed'),
  });

  // NEW: SEO Audit
  const [audit, setAudit] = React.useState<any[]>([]);
  const runAudit = useMutation({
    mutationFn: async () => postJSON({ action: 'audit' }),
    onSuccess: (j: any) => {
      setAudit(Array.isArray(j.audits) ? j.audits : []);
      setNote('Audit complete.');
    },
    onError: (e: any) => setNote(e?.message || 'Audit failed'),
  });

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border bg-white p-4 shadow">
        <h2 className="text-lg font-semibold mb-2">robots.txt</h2>
        <p className="text-sm text-gray-600 mb-2">
          This controls what crawlers can access. Editing here updates the live <a className="text-blue-600 underline" href="/api/seo?action=robots.render" target="_blank" rel="noreferrer">/robots.txt</a>.
        </p>
        <textarea
          value={robots}
          onChange={(e) => setRobots(e.target.value)}
          className="w-full h-48 rounded-xl border p-2 font-mono text-sm"
          placeholder={`User-agent: *\nAllow: /\nDisallow: /EdibleArtControl\nDisallow: /theme-preview\nDisallow: /seo-maintenance`}
        />
        <div className="mt-3 flex items-center gap-2">
          <button
            className="rounded-xl border px-3 py-2 hover:bg-gray-50"
            onClick={() => saveRobots.mutate()}
            disabled={saveRobots.isLoading}
          >
            {saveRobots.isLoading ? 'Saving…' : 'Save robots.txt'}
          </button>
          <a
            className="text-sm text-blue-600 underline"
            href="/api/seo?action=robots.render"
            target="_blank"
            rel="noreferrer"
          >
            View live robots.txt
          </a>
        </div>
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow">
        <h2 className="text-lg font-semibold mb-2">XML Sitemap</h2>
        <p className="text-sm text-gray-600 mb-2">
          Generated from your Pages where <code>index</code> is enabled.
        </p>
        <div className="flex items-center gap-2">
          <button
            className="rounded-xl border px-3 py-2 hover:bg-gray-50"
            onClick={() => genSitemap.mutate()}
            disabled={genSitemap.isLoading}
          >
            {genSitemap.isLoading ? 'Generating…' : 'Generate sitemap'}
          </button>
          <a className="text-sm text-blue-600 underline" href="/api/seo?action=sitemap.render" target="_blank" rel="noreferrer">View live /sitemap.xml</a>
          <a className="text-sm text-blue-600 underline" href="/api/seo?action=sitemap.render&fresh=1" target="_blank" rel="noreferrer">Force refresh</a>
        </div>
      </section>

      {/* NEW: SEO Audit */}
      <section className="rounded-2xl border bg-white p-4 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold mb-2">SEO Audit</h2>
          <button
            className="rounded-xl border px-3 py-2 hover:bg-gray-50"
            onClick={() => runAudit.mutate()}
            disabled={runAudit.isLoading}
          >
            {runAudit.isLoading ? 'Running…' : 'Run audit'}
          </button>
        </div>
        {audit.length === 0 ? (
          <p className="text-sm text-gray-600">No issues found yet. Click “Run audit”.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {audit.map((a, i) => (
              <li key={i} className="rounded border px-2 py-1">
                <code>{a.type}</code>{' '}
                {a.slug && <span className="text-gray-700">· {a.slug}</span>}
                {a.title && <span className="text-gray-700">· “{a.title}”</span>}
                {Array.isArray(a.slugs) && a.slugs.length > 1 && (
                  <span className="text-gray-500"> · {a.slugs.join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {note && <p className="text-sm text-emerald-700">{note}</p>}
    </div>
  );
}